import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCookie } from '@cian/utils/lib/browser/cookie';
import { ConsultantWidgetDesktop, NewbuildingChatMobile, prepareUserData } from '@cian/newbuilding-chat-component';
import { useDeviceType } from '@cian/ui-kit/responsive';

import { ChatMobile } from '../../components/ChatMobile';
import { trackDesktopChatVisible, trackDesktopChatClose } from './tracking';
import { WEBIM_INFORMER_VISIBLE, WEBIM_LOCATION } from '../../constants/config/webim';
import { toggleVisibleInformer } from '../../actions/webim';
import { selectUser } from '../../selectors/user';

export interface IChatContainerProps {
  accountName: string;
  host?: string;
  webimHash: string;
  isVisibleInformer: boolean;
}

export const ChatContainer: React.FC<IChatContainerProps> = ({ accountName, host, webimHash, isVisibleInformer }) => {
  const dispatch = useDispatch();
  const isDesktop = useDeviceType() === 'desktop';
  const [isOpen, setOpened] = useState(false);
  const [ready, setReady] = useState(false);
  const { userId } = useSelector(selectUser);

  const webimProps = useMemo(
    () => ({
      accountName,
      host,
      userData: prepareUserData({ userId, displayName: null, webimHash }),
      location: WEBIM_LOCATION,
    }),
    [accountName, host, userId, webimHash],
  );

  const onReady = useCallback(() => setReady(true), []);
  const toggleOpen = useCallback((isOpen: boolean) => {
    setOpened(isOpen);

    /* istanbul ignore next */
    if (isOpen) {
      trackDesktopChatVisible();
    } else {
      trackDesktopChatClose();
    }
  }, []);
  const onCloseInformer = useCallback(() => {
    dispatch(toggleVisibleInformer(false));

    setCookie(WEBIM_INFORMER_VISIBLE, '1');
  }, [dispatch]);

  /**
   * При маунте чатов и их готовности сразу открываем диалог
   */
  useEffect(() => {
    if (ready) {
      setOpened(true);
      trackDesktopChatVisible();
    }
  }, [ready]);

  return isDesktop ? (
    <ConsultantWidgetDesktop
      chatOpened={isOpen}
      onReady={onReady}
      toggleOpen={toggleOpen}
      webimProps={webimProps}
      messengersLinks={[]}
    />
  ) : (
    <ChatMobile isOpenInformer={isVisibleInformer} onCloseInformer={onCloseInformer}>
      <NewbuildingChatMobile
        location={webimProps.location}
        accountName={webimProps.accountName}
        host={webimProps.host}
        userData={webimProps.userData}
      />
    </ChatMobile>
  );
};

ChatContainer.displayName = 'ChatContainer';
